import type { BadgeTone } from "@sagot/ui";
import type { Lead, Stage, StageIn } from "./lead-types";

/** Backend limits (app/core/constants/inbox.py LEAD_STAGES_MIN/MAX, LEAD_STAGE_NAME_MAX). */
export const STAGES_MIN = 2;
export const STAGES_MAX = 12;
export const STAGE_NAME_MAX = 40;
const MASK_CHAR = "•";

export type StageOutcome = "open" | "won" | "lost";

export interface StageDraft {
  key: string;
  id: string | null;
  name: string;
  outcome: StageOutcome;
  leadCount: number;
}

export type StageError =
  | { code: "nameRequired" }
  | { code: "duplicate" }
  | { code: "needWon" }
  | { code: "needLost" }
  | { code: "tooFew" }
  | { code: "tooMany" }
  | { code: "reassignRequired"; stage: string };

export function toDrafts(stages: readonly Stage[]): StageDraft[] {
  return [...stages]
    .sort((a, b) => a.position - b.position)
    .map((s) => ({ key: s.id, id: s.id, name: s.name, outcome: s.is_won ? "won" : s.is_lost ? "lost" : "open", leadCount: s.lead_count }));
}

/** Stages present before editing that are gone from the drafts and still hold leads. */
export function removedWithLeads(original: readonly StageDraft[], drafts: readonly StageDraft[]): StageDraft[] {
  const kept = new Set(drafts.map((d) => d.id).filter(Boolean));
  return original.filter((s) => s.id !== null && !kept.has(s.id) && s.leadCount > 0);
}

/** Client mirror of the pipeline rules (DEC-031): ≥ 1 won, ≥ 1 lost, unique names, reassignment. */
export function validateStages(original: readonly StageDraft[], drafts: readonly StageDraft[], reassign: Readonly<Record<string, string>>): StageError[] {
  const errors: StageError[] = [];
  const names = drafts.map((d) => d.name.trim().toLocaleLowerCase());
  if (names.some((n) => n === "")) errors.push({ code: "nameRequired" });
  if (new Set(names).size !== names.length) errors.push({ code: "duplicate" });
  if (drafts.length < STAGES_MIN) errors.push({ code: "tooFew" });
  if (drafts.length > STAGES_MAX) errors.push({ code: "tooMany" });
  if (!drafts.some((d) => d.outcome === "won")) errors.push({ code: "needWon" });
  if (!drafts.some((d) => d.outcome === "lost")) errors.push({ code: "needLost" });
  for (const stage of removedWithLeads(original, drafts)) {
    if (!stage.id || !reassign[stage.id]) errors.push({ code: "reassignRequired", stage: stage.name });
  }
  return errors;
}

export function toStagesPut(drafts: readonly StageDraft[], reassign: Readonly<Record<string, string>>): { stages: StageIn[]; reassign: Record<string, string> } {
  return {
    stages: drafts.map((d) => ({ ...(d.id ? { id: d.id } : {}), name: d.name.trim(), is_won: d.outcome === "won", is_lost: d.outcome === "lost" })),
    reassign: { ...reassign },
  };
}

/** Moves the item at `index` one step in `direction`; returns a new array (no-op at the ends). */
export function moveItem<T>(items: readonly T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (index < 0 || target < 0 || target >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(index, 1);
  if (item !== undefined) next.splice(target, 0, item);
  return next;
}

/** Keyboard alternative to drag and drop: the neighbouring stage (null at the ends). */
export function adjacentStage(stages: readonly Stage[], currentId: string, direction: -1 | 1): Stage | null {
  const ordered = [...stages].sort((a, b) => a.position - b.position);
  const index = ordered.findIndex((s) => s.id === currentId);
  if (index < 0) return null;
  return ordered[index + direction] ?? null;
}

/** The API masks mobiles for members without `leads.export` (DEC-031): "•••• ••• 1048". */
export function isMaskedMobile(mobile: string | null | undefined): boolean {
  return Boolean(mobile?.includes(MASK_CHAR));
}

/** International numbers shown as stored (E.164, any country — DEC-041); masked values pass through. */
export function displayMobile(mobile: string | null | undefined): string {
  return mobile?.trim() ?? "";
}

/** Badge tone per pipeline position (first = brand, won = success, lost = danger). */
export function stageTone(stage: Pick<Stage, "position" | "is_won" | "is_lost">): BadgeTone {
  if (stage.is_won) return "success";
  if (stage.is_lost) return "danger";
  if (stage.position === 0) return "brand";
  return stage.position === 1 ? "neutral" : "warning";
}

/** Interest shown in lists: the tenant's lead-form segment when present, else the free-text interest. */
export function leadInterest(lead: Pick<Lead, "custom_fields" | "interest">): string {
  const segment = lead.custom_fields.segment;
  return typeof segment === "string" && segment ? segment : (lead.interest ?? "");
}

export function distinctInterests(leads: readonly Pick<Lead, "interest">[]): string[] {
  return [...new Set(leads.map((l) => l.interest).filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b));
}
